import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import './AuthComponent.css';

export default function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [sessionReady, setSessionReady] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const handlePasswordReset = async () => {
      try {
        // Get all possible URL parameters
        const accessToken = searchParams.get('access_token');
        const refreshToken = searchParams.get('refresh_token');
        const code = searchParams.get('code');
        const type = searchParams.get('type');
        const tokenHash = searchParams.get('token_hash');
        
        console.log('URL Parameters:', { accessToken, refreshToken, code, type, tokenHash });
        console.log('Full URL:', window.location.href);
        
        // Check if this is a password recovery type
        if (type === 'recovery' || type === 'password_recovery') {
          if (accessToken && refreshToken) {
            // Handle token-based recovery
            const { data, error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken
            });
            
            if (error) {
              console.error('Error setting session with tokens:', error);
              setError('This reset link has expired or is invalid. Please request a new password reset.');
              return;
            }
            
            if (data.session) {
              console.log('Session set successfully with tokens');
              setSessionReady(true);
              return;
            }
          }
        }
        
        // Handle PKCE flow with code
        if (code) {
          const { data, error } = await supabase.auth.exchangeCodeForSession(code);
          
          if (error) {
            console.error('Error exchanging code for session:', error);
            setError('This reset link has expired or is invalid. Please request a new password reset.');
            return;
          }
          
          if (data.session) {
            console.log('Session set successfully with code');
            setSessionReady(true);
            return;
          }
        }
        
        // Check if we have a fragment-based URL (older Supabase format)
        const fragment = window.location.hash.substring(1);
        if (fragment) {
          const fragmentParams = new URLSearchParams(fragment);
          const fragmentAccessToken = fragmentParams.get('access_token');
          const fragmentRefreshToken = fragmentParams.get('refresh_token');
          const fragmentType = fragmentParams.get('type');
          
          console.log('Fragment Parameters:', { fragmentAccessToken, fragmentRefreshToken, fragmentType });
          
          if (fragmentType === 'recovery' && fragmentAccessToken && fragmentRefreshToken) {
            const { data, error } = await supabase.auth.setSession({
              access_token: fragmentAccessToken,
              refresh_token: fragmentRefreshToken
            });
            
            if (error) {
              console.error('Error setting session with fragment tokens:', error);
              setError('This reset link has expired or is invalid. Please request a new password reset.');
              return;
            }
            
            if (data.session) {
              console.log('Session set successfully with fragment tokens');
              setSessionReady(true);
              return;
            }
          }
        }
        
        // Try to get the current session (user might already be authenticated)
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('Error getting current session:', sessionError);
        } else if (sessionData.session) {
          console.log('Found existing session');
          setSessionReady(true);
          return;
        }
        
        // If no valid parameters found
        console.log('No valid reset parameters found');
        setError('Invalid reset link. Please click the link from your password reset email, or request a new reset from the sign-in page.');
        
      } catch (err) {
        console.error('Error handling password reset:', err);
        setError('Something went wrong processing your reset link. Please try requesting a new password reset.');
      }
    };

    handlePasswordReset();
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    if (!sessionReady) {
      setError('Session not ready. Please click the reset link from your email again.');
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      setLoading(false);
      return;
    }

    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) throw error;

      setSuccess('Password updated successfully! Redirecting to sign in...');
      
      // Sign out and redirect to login page after a short delay
      setTimeout(async () => {
        await supabase.auth.signOut();
        navigate('/login');
      }, 2000);
      
    } catch (error) {
      console.error('Error updating password:', error);
      setError('Failed to update password. Please try again or request a new reset link.');
    } finally {
      setLoading(false);
    }
  };

  if (!sessionReady && !error) {
    return (
      <div className="auth-container forgot-mode">
        <div className="auth-card">
          <div className="auth-header">
            <h2>Setting up password reset...</h2>
            <p className="auth-subheader">
              Please wait while we verify your reset link.
            </p>
          </div>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div className="spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container forgot-mode">
      <div className="auth-card">
        <div className="auth-header">
          <h2>Set New Password</h2>
          <p className="auth-subheader">
            Enter your new password below. Make sure it's at least 6 characters long.
          </p>
        </div>

        {error && (
          <div className="auth-error">
            {error}
            <div style={{ marginTop: '1rem', fontSize: '0.9rem', lineHeight: '1.5' }}>
              <strong>Troubleshooting steps:</strong>
              <ol style={{ marginTop: '0.5rem', paddingLeft: '1.5rem', textAlign: 'left' }}>
                <li>Make sure you clicked the link directly from your email</li>
                <li>Check if the email link has expired (links expire after 1 hour)</li>
                <li>Try copying and pasting the full URL from your email</li>
                <li>Request a new password reset if the link is old</li>
              </ol>
            </div>
            <div style={{ marginTop: '1rem' }}>
              <button 
                onClick={() => navigate('/login')}
                className="auth-toggle-button"
                style={{ fontSize: '0.9rem' }}
              >
                Request new password reset
              </button>
            </div>
          </div>
        )}
        {success && <div className="auth-success">{success}</div>}
        
        {sessionReady && !success && (
          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="password">New Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter your new password"
                minLength={6}
                disabled={loading}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="Confirm your new password"
                minLength={6}
                disabled={loading}
              />
            </div>
            
            <button 
              type="submit" 
              disabled={loading}
              className="auth-submit forgot-button"
            >
              {loading ? 'Updating Password...' : 'Update Password'}
            </button>
          </form>
        )}
        
        <div className="auth-footer">
          <p>
            <button 
              type="button"
              className="auth-toggle-button"
              onClick={() => navigate('/login')}
            >
              Back to Sign In
            </button>
          </p>
        </div>
      </div>
    </div>
  );
} 